import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClipboardList,
  CheckCircle,
  XCircle,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { CreateReportTypeModal } from './CreateReportTypeModal';
import { ReportTypeDetailModal } from './ReportTypeDetailModal';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { localizedName } from '@/utils/localized';
import type { ReportTypeDefinition, ReportTypeListResponse } from '@/features/reports/types';
import { useDeactivateReportType } from '@/features/reports/hooks/useReportTypes';
import { useModal } from '@/components/modal';
import toast from 'react-hot-toast';
import { PageHeader, StatCard, CardSkeleton, EmptyState, ErrorState, SearchInput, GlassSelect, CreateButton, BulkActionBar, StatusDot, InfiniteScrollSentinel } from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════
export function ReportTypesManagementPage() {
  const { t, i18n } = useTranslation();
  const isRTL = useRTL();
  const queryClient = useQueryClient();

  const { hasPermission } = usePermissions();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ReportTypeDefinition | null>(null);

  const deleteMutation = useDeactivateReportType();
  const { openConfirm } = useModal();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['reportTypes', 'infinite', search, statusFilter],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter;

      const { data } = await http.get<ReportTypeListResponse>('/report-types', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.reportTypes.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allTypes = useMemo(
    () => data?.pages.flatMap((page) => page.reportTypes) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allTypes);

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allTypes.filter((rt) => rt.isActive).length,
    [allTypes]
  );
  const inactiveCount = useMemo(
    () => allTypes.filter((rt) => !rt.isActive).length,
    [allTypes]
  );

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('reportTypesAdmin.bulkDeleteTitle'),
      message: t('reportTypesAdmin.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
        toast.success(t('reportTypesAdmin.bulkDeletedSuccess'));
        clear();
      },
    });
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* ─── Header ──────────────────────────────────────────── */}
      <motion.div variants={staggerItem}>
        <PageHeader
          icon={<ClipboardList className="w-7 h-7" />}
          title={t('reportTypesAdmin.management')}
          action={
            hasPermission('action:manage_treatments') ? (
              <CreateButton
                label={t('reportTypesAdmin.addType')}
                onClick={() => {
                  triggerHapticFeedback();
                  setCreateModalOpen(true);
                }}
              />
            ) : undefined
          }
        />
      </motion.div>

      {/* ─── Stats Cards ─────────────────────────────────────── */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<ClipboardList className="w-7 h-7 md:w-8 md:h-8" />} label={t('reportTypesAdmin.totalTypes')} value={totalCount} />
        <StatCard icon={<CheckCircle className="w-7 h-7 md:w-8 md:h-8" />} label={t('reportTypesAdmin.active')} value={activeCount} />
        <StatCard icon={<XCircle className="w-7 h-7 md:w-8 md:h-8" />} label={t('reportTypesAdmin.inactive')} value={inactiveCount} />
      </motion.div>

      {/* ─── Search & Filters ────────────────────────────────── */}
      <motion.div
        variants={staggerItem}
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h2 className="font-semibold text-xs sm:text-sm">
            {t('common.filters')}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col md:flex-row gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder={t('reportTypesAdmin.searchPlaceholder')} />

          {hasPermission('action:manage_treatments') && (
            <button onClick={selectAll} className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors">
              <input type="checkbox" checked={allSelected} onChange={selectAll} className="w-5 h-5 pointer-events-none" readOnly />
              <span className="font-bold text-sm">{t('reportTypesAdmin.selectAll')}</span>
            </button>
          )}

          <GlassSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('reportTypesAdmin.allStatuses')}</option>
            <option value="true">{t('reportTypesAdmin.active')}</option>
            <option value="false">{t('reportTypesAdmin.inactive')}</option>
          </GlassSelect>
        </div>
      </motion.div>

      {/* ─── Report Type List ──────────────────────────────── */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('reportTypesAdmin.loadError')} />
        ) : allTypes.length === 0 ? (
          <EmptyState icon={<ClipboardList className="w-14 h-14 md:w-16 md:h-16" />} message={t('reportTypesAdmin.noTypes')} />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allTypes.map((rt) => {
              const selected = isSelected(rt.id);

              return (
                <motion.div
                  key={rt.id}
                  role="button"
                  tabIndex={0}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    triggerHapticFeedback();
                    setSelectedType(rt);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      triggerHapticFeedback();
                      setSelectedType(rt);
                    }
                  }}
                  className="flex items-center justify-between w-full py-1.5 ps-6 pe-4 md:py-2 md:ps-6 md:pe-4 rounded-xl text-start cursor-pointer border border-white/30 glass-surface"
                  style={{ boxShadow: selected ? ADMIN_COLORS.shadowMd : undefined }}
                >
                  {/* Left section */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Checkbox */}
                    {hasPermission('action:manage_treatments') && (
                      <div
                        className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                        style={{ minWidth: '44px', minHeight: '44px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(rt.id);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggle(rt.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-5 h-5 pointer-events-none"
                        />
                      </div>
                    )}
                    {/* Icon circle */}
                    <div
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{
                        backgroundColor: ADMIN_COLORS.surface,
                        border: `1px solid ${ADMIN_COLORS.border}`,
                      }}
                    >
                      <ClipboardList className="w-5 h-5 md:w-6 md:h-6" style={{ color: ADMIN_COLORS.navy }} />
                    </div>

                    {/* Text content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs md:text-sm text-neutral-800 truncate max-w-[160px] md:max-w-none">
                        {localizedName(rt, i18n.language)}
                      </p>
                    </div>
                  </div>

                  {/* Right section */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Status dot */}
                    <StatusDot
                      active={rt.isActive}
                      title={rt.isActive ? t('reportTypesAdmin.active') : t('reportTypesAdmin.inactive')}
                    />

                    {/* Chevron */}
                    <ChevronRight
                      className="w-5 h-5 text-slate-500"
                      style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Infinite scroll sentinel ─────────────────────────── */}
      <InfiniteScrollSentinel hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} sentinelRef={sentinelRef} />

      {/* ─── Modals ───────────────────────────────────────────── */}
      <CreateReportTypeModal
        isOpen={createModalOpen}
        onClose={() => { queryClient.refetchQueries({ queryKey: ['reportTypes'] }); setCreateModalOpen(false); }}
        onSuccess={() => {}}
      />

      {selectedType && (
        <ReportTypeDetailModal
          reportType={selectedType}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['reportTypes'] }); setSelectedType(null); }}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar count={count} onDelete={handleBulkDelete} onClear={clear} isDeleting={deleteMutation.isPending} />
    </motion.div>
  );
}
